import React, { createContext, FC, useContext, useState } from 'react';
import { Gender } from 'utils/types/common';

type AuthContext = {
  authState: AuthState;
  sessionState: string;
  logout: () => void;
  handleParentAuth: (token: string, age?: number, gender?: Gender) => void;
  handleSessionUpdate: (session: string) => void;
};
type AuthError = {
  type: string;
  message?: string;
};

export type AuthState =
  | { type: 'NotLoggedIn' }
  | { type: 'Loading' }
  | { type: 'LoggedIn'; accessToken?: string; age?: number; gender?: Gender }
  | { type: 'Error'; error: AuthError };

const AuthContext = createContext<AuthContext | null>(null);

export const AuthProvider: FC = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({ type: 'NotLoggedIn' });
  const [sessionState, setSessionState] = useState<string>('');

  function logout() {
    setAuthState({ type: 'NotLoggedIn' });
    return;
  }

  function handleParentAuth(token: string, age?: number, gender?: Gender) {
    setAuthState({ type: 'LoggedIn', accessToken: token, age, gender });
    return;
  }

  function handleSessionUpdate(session: string) {
    setSessionState(session);
    return;
  }

  return (
    <AuthContext.Provider
      value={{
        authState,
        sessionState,
        logout,
        handleParentAuth,
        handleSessionUpdate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContext {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('This component cannot be rendered without AuthContext being available');
  return ctx;
}
